/* import __COLOCATED_TEMPLATE__ from './otp-confirmation-modal.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type NotificationsService from 'embercom/services/notifications-service';

interface Args {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onConfirm: (otpCode: string) => void;
  isLoading?: boolean;
  errorMessage?: string;
  customValidator?: (code: string) => boolean;
  onClearError?: () => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

type InputMode = 'otp' | 'recovery';

export default class OtpConfirmationModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;

  @tracked otpCode = '';
  @tracked inputMode: InputMode = 'otp';
  _wasOpenPreviously = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this._wasOpenPreviously = this.args.isOpen;
  }

  get title() {
    return this.args.title || this.intl.t('apps.app.account.two-factor-auth.enter-code');
  }

  @action
  handleModalStateChange() {
    if (this.args.isOpen && !this._wasOpenPreviously) {
      this.otpCode = '';
      this.inputMode = 'otp';
      this._wasOpenPreviously = true;
    } else if (!this.args.isOpen) {
      this._wasOpenPreviously = false;
    }
  }

  get description() {
    if (this.args.description) {
      return this.args.description;
    }

    if (this.inputMode === 'recovery') {
      return this.intl.t('apps.app.account.two-factor-auth.recovery-code-description');
    }

    return this.intl.t('apps.app.account.two-factor-auth.enter-code-from-authenticator');
  }

  get placeholder() {
    if (this.inputMode === 'recovery') {
      return this.intl.t('apps.app.account.two-factor-auth.eight-character');
    }
    return this.intl.t('apps.app.account.two-factor-auth.six-digit');
  }

  get maxLength() {
    return this.inputMode === 'recovery' ? 8 : 6;
  }

  get pattern() {
    return this.inputMode === 'recovery' ? '[A-Za-z0-9]{8}' : '[0-9]{6}';
  }

  get isOtpMode() {
    return this.inputMode === 'otp';
  }

  get isRecoveryMode() {
    return this.inputMode === 'recovery';
  }

  get confirmButtonText() {
    return (
      this.args.confirmButtonText || this.intl.t('apps.app.account.two-factor-auth.validate-button')
    );
  }

  get cancelButtonText() {
    return this.args.cancelButtonText || this.intl.t('apps.app.account.two-factor-auth.cancel');
  }

  get isOtpValid() {
    if (this.args.customValidator) {
      return this.args.customValidator(this.otpCode);
    }

    if (this.inputMode === 'recovery') {
      return this.otpCode?.length === 8;
    }

    return this.otpCode?.length === 6;
  }

  get isConfirmButtonDisabled() {
    return !this.isOtpValid || this.args.isLoading;
  }

  @action
  onModalClose() {
    this.otpCode = '';
    this.inputMode = 'otp';
    this.args.onClose();
  }

  @action
  onConfirm() {
    if (this.isOtpValid) {
      this.args.onConfirm(this.otpCode);
    } else {
      let warningMessage =
        this.inputMode === 'recovery'
          ? this.intl.t('apps.app.account.two-factor-auth.eight-character-length')
          : this.intl.t('apps.app.account.two-factor-auth.six-digit-length');
      this.notificationsService.notifyWarning(warningMessage);
    }
  }

  @action
  onCancel() {
    this.onModalClose();
  }

  @action
  updateOtpCode(event: Event) {
    let target = event.target as HTMLInputElement;

    if (this.inputMode === 'recovery') {
      let alphanumericValue = target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
      this.otpCode = alphanumericValue;
    } else {
      let numericValue = target.value.replace(/\D/g, '').slice(0, 6);
      this.otpCode = numericValue;
    }
  }

  @action
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.isOtpValid && !this.args.isLoading) {
        this.onConfirm();
      }
    }
  }

  @action
  switchToRecoveryMode() {
    this.inputMode = 'recovery';
    this.otpCode = '';
    if (this.args.onClearError) {
      this.args.onClearError();
    }
  }

  @action
  switchToOtpMode() {
    this.inputMode = 'otp';
    this.otpCode = '';
    if (this.args.onClearError) {
      this.args.onClearError();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Common::OtpConfirmationModal': typeof OtpConfirmationModal;
    'common/otp-confirmation-modal': typeof OtpConfirmationModal;
  }
}
